import React, { useMemo, useState } from 'react';
import {
  GetViewerLearningDetailsQuery,
  CourseEnrollmentStatus,
  ClassEnrollmentStatus,
} from '@sandtech-hub-monorepo/ehub-client';
import { Switch } from '@sandtech-hub-monorepo/ui-kit';
import { PlannedCourseView } from './PlannedCourseView';
import { CoursesGroupView } from './CoursesGroupView';

type PlannedCoursesViewType = NonNullable<
  NonNullable<GetViewerLearningDetailsQuery['viewer']['programEnrollments']>
>[number]['plannedCourses'];

type PlannedCoursesViewProps = {
  plannedCourses: PlannedCoursesViewType;
  onlyOngoingCourses?: boolean;
  showOngoingCoursesToggle?: boolean;
  coursesHiddenByDefault?: boolean;
  isUniversityProgram?: boolean;
};

export const PlannedCoursesView = ({
  plannedCourses,
  onlyOngoingCourses = false,
  showOngoingCoursesToggle = false,
  coursesHiddenByDefault = false,
  isUniversityProgram = false,
}: PlannedCoursesViewProps) => {
  const [toggleOnlyOngoing, setToggleOnlyOngoing] = useState(true);
  const [showCourses, setShowCourses] = useState(!coursesHiddenByDefault);
  const filterToOngoing = showOngoingCoursesToggle
    ? toggleOnlyOngoing
    : onlyOngoingCourses;

  const ongoingCourses =
    plannedCourses?.filter(
      (x) =>
        x.enrollment && x.enrollment.status === CourseEnrollmentStatus.Ongoing
    ) ?? [];

  const visibleCourses = filterToOngoing ? ongoingCourses : plannedCourses;

  const groupedPlannedCoursesEntries = useMemo(() => {
    const group = {} as Record<string, PlannedCoursesViewType>;
    visibleCourses.forEach((course) => {
      const key =
        course.enrollment?.currentClass?.class?.term ??
        course.courseGroupKey ??
        '';
      if (!group[key]) group[key] = [];
      group[key].push(course);
    });
    return Object.entries(group).map(([key, courses]) => ({
      key,
      courses,
      totalCredits: courses.reduce((sum, c) => sum + (c.credits ?? 0), 0),
      isCompleted: courses.every(
        (c) =>
          c.enrollment?.currentClass?.status === ClassEnrollmentStatus.Graduated ||
          c.enrollment?.status === CourseEnrollmentStatus.Graduated
      ),
    }));
  }, [visibleCourses]);

  const renderPlannedCourse = (
    plannedCourse: PlannedCoursesViewType[number]
  ) => (
    <PlannedCourseView
      plannedCourse={plannedCourse}
      key={plannedCourse.id}
      variant={isUniversityProgram ? 'university' : 'default'}
    />
  );

  return (
    <div className="w-full mt-4">
      {!isUniversityProgram && (
        <div className="border-b-0 border-t border-border pb-4" />
      )}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="text-left text-sm font-normal pb-0 hover:no-underline">
          {coursesHiddenByDefault
            ? `${plannedCourses.length} course${
                plannedCourses.length === 1 ? '' : 's'
              } in this program`
            : `You have ${
                ongoingCourses.length ? ongoingCourses.length : 'no'
              } active course${
                ongoingCourses.length === 1 ? '' : 's'
              } in this program`}
        </div>
        {showOngoingCoursesToggle && (
          <label className="flex items-center gap-2 text-sm font-medium text-foreground cursor-pointer">
            <Switch
              checked={toggleOnlyOngoing}
              onCheckedChange={setToggleOnlyOngoing}
            />
            Active only
          </label>
        )}
        {coursesHiddenByDefault && (
          <label className="flex items-center gap-2 text-sm font-medium text-foreground cursor-pointer">
            <Switch checked={showCourses} onCheckedChange={setShowCourses} />
            Show courses
          </label>
        )}
      </div>
      {showCourses &&
        (groupedPlannedCoursesEntries.length === 1
          ? groupedPlannedCoursesEntries[0].courses.map(renderPlannedCourse)
          : groupedPlannedCoursesEntries.map(({ key, courses, totalCredits, isCompleted }, index) => (
              <CoursesGroupView
                key={key}
                name={key}
                totalCredits={isUniversityProgram ? totalCredits : undefined}
                isCompleted={isUniversityProgram ? isCompleted : undefined}
                initialExpanded={index === 0}
                isLastElement={index === groupedPlannedCoursesEntries.length - 1}
              >
                {courses.map(renderPlannedCourse)}
              </CoursesGroupView>
            )))}
    </div>
  );
};
